import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { AlertTriangle, CheckCircle, User, Package, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export default function AdminIssuesTab() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    adminAPI.getDeliveryIssues().then(r => setIssues(r.data.issues || [])).catch(() => toast.error('Erreur')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleResolve = async (issueId) => {
    const note = window.prompt('Note de résolution (visible par l\'acheteur) :');
    if (note === null) return;
    try {
      await adminAPI.resolveDeliveryIssue(issueId, { resolution: 'resolved', note });
      toast.success('Signalement résolu');
      load();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-bold text-[#033871]">Problèmes de livraison</h2>
      {loading ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-3 border-[#0C63BF] border-t-transparent rounded-full animate-spin" /></div>
      ) : issues.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E8F4FD] p-10 text-center">
          <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
          <p className="text-sm text-[#5A8BA8]">Aucun problème de livraison signalé.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {issues.map(issue => (
            <div key={issue.id} className={`bg-white rounded-2xl border p-4 ${issue.status === 'open' ? 'border-amber-200' : 'border-[#E8F4FD] opacity-70'}`} data-testid={`issue-${issue.id}`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${issue.status === 'open' ? 'text-amber-500' : 'text-emerald-500'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${issue.status === 'open' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {issue.status === 'open' ? 'En attente' : 'Résolu'}
                    </span>
                    <span className="text-[10px] text-[#5A8BA8]">{new Date(issue.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                  <div className="mt-2 text-sm text-[#033871] flex items-center gap-1">
                    <Package className="w-3.5 h-3.5 text-[#0C63BF]" />
                    <span className="font-semibold">{issue.order_details?.brand} {issue.order_details?.model}</span>
                    <span className="text-[#5A8BA8]">({issue.order_details?.total?.toFixed(2)}€)</span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-[#5A8BA8]">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" /> Acheteur: {issue.buyer?.first_name} {issue.buyer?.last_name}</span>
                    <span className="flex items-center gap-1"><User className="w-3 h-3" /> Vendeur: {issue.seller?.first_name} {issue.seller?.last_name}</span>
                  </div>
                  <div className="mt-2 bg-[#F7FAFB] rounded-lg p-3">
                    <p className="text-xs text-[#5A8BA8] flex items-center gap-1 mb-1"><MessageSquare className="w-3 h-3" /> Motif :</p>
                    <p className="text-sm text-[#033871]">{issue.reason}</p>
                  </div>
                  {issue.admin_note && (
                    <div className="mt-2 bg-emerald-50 rounded-lg p-3">
                      <p className="text-xs text-emerald-600 mb-1">Réponse admin :</p>
                      <p className="text-sm text-emerald-700">{issue.admin_note}</p>
                    </div>
                  )}
                </div>
                {issue.status === 'open' && (
                  <Button size="sm" onClick={() => handleResolve(issue.id)} className="bg-emerald-500 hover:bg-emerald-600 shrink-0" data-testid={`resolve-issue-${issue.id}`}>
                    Résoudre
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
